import React, { useMemo } from 'react';

import { t } from '../../i18n/translator';
import type { SpinWheelWildReward } from '../../services/spinWheel.service';
import styles from '../../styles/components/spin-wheel-popup.module.scss';

type Props = {
  balance: number;
  catalog: SpinWheelWildReward[];
  onOpenMarket: () => void;
  available: boolean;
  loading?: boolean;
  /**
   * Çarkta wild ödülü kazandığında popup tarafından kısa süreliğine `true`
   * olur. Pillar kendi `data-wild-incoming` attribute'unu set eder; SCSS o
   * durumda hem frame'de altın parıltı (`wildPillarReceive`) hem fill'de
   * brightness pulse (`wildFillBump`) atar — kazanılan wild'ın bara "emildiği"
   * hissini destekler. 1.4–1.6sn sonra parent flag'i temizler ve animasyonlar
   * otomatik biter.
   */
  wildIncoming?: boolean;
};

/**
 * En ucuz (takas edilebilir) Wild ödülünü bulur. Aktif olmayan veya stoku
 * tükenmiş ödüller hedef olarak seçilmez — kullanıcı ulaşamayacağı bir hedefe
 * doğru ilerlemiş gibi hissetmesin. Stok null ise sınırsız kabul edilir.
 */
function findCheapestReward(catalog: SpinWheelWildReward[]): SpinWheelWildReward | null {
  const available = catalog.filter(
    (reward) => reward.active && (reward.stock === null || reward.stock > 0)
  );
  if (available.length === 0) return null;
  return available.reduce((min, reward) => (reward.wildCost < min.wildCost ? reward : min));
}

/**
 * Wild Progress Pillar — minimal "enerji barı" varyantı.
 *
 * UX kararları (önceki ornate plaque'tan farklılıklar):
 * - İçerideki sayı bloğu (`balance/target Wild`) ve "HEDEF/HAZIR" alt etiketi
 *   kaldırıldı — bar metaforu tek başına ilerlemeyi anlatsın, metin tıkanıklığı
 *   olmasın. Detaylar tooltip + market modal'da yaşar.
 * - Komple aside artık `<button>` — pillar'ın herhangi bir noktasına tıklamak
 *   market'i açar. Eski "Market" alt-butonu yerini sadece dekoratif üst ikona
 *   bıraktı.
 * - Hedef hâlâ en ucuz ödül (`findCheapestReward`); UI'da görünmüyor ama
 *   bar yüzdesi onun üzerinden hesaplanıyor (her zaman bir sonraki erişilebilir
 *   ödülü hedefler).
 *
 * Mimari notlar:
 * - Sıvı animasyonları (yüzey dalgası, shimmer, 4 bubble) CSS'de — burası
 *   sadece `pct` ve `data-state` üretir.
 * - `data-state="ready"` ulaşıldığında frame parlar + ikon pulse atar
 *   ("takas zamanı" sinyali).
 */
const WildProgressBar: React.FC<Props> = ({
  balance,
  catalog,
  onOpenMarket,
  available,
  loading = false,
  wildIncoming = false,
}) => {
  const cheapest = useMemo(() => findCheapestReward(catalog), [catalog]);
  const clampedBalance = Math.max(0, balance);
  const target = cheapest?.wildCost ?? 0;
  const pct = target > 0 ? Math.min(100, (clampedBalance / target) * 100) : 0;
  const isReady = cheapest !== null && clampedBalance >= cheapest.wildCost;

  const stateAttr: 'empty' | 'progress' | 'ready' = !cheapest
    ? 'empty'
    : isReady
      ? 'ready'
      : 'progress';

  // Erişilebilirlik: tooltip + aria-label birleşik. Görsel olarak hiçbir metin
  // yok ama screen reader + hover hâlâ "ne kadar ilerlediğimi" söylüyor.
  const tooltipText = !available
    ? loading
      ? t(2525)
      : t(2526)
    : cheapest
      ? isReady
        ? t(2527, { values: { reward: cheapest.label } })
        : t(2528, {
            values: {
              balance: clampedBalance,
              target: cheapest.wildCost,
              reward: cheapest.label,
            },
          })
      : t(2450);

  return (
    <button
      type="button"
      className={styles.wildPillar}
      data-state={stateAttr}
      data-wild-incoming={wildIncoming ? 'true' : undefined}
      aria-label={tooltipText}
      title={tooltipText}
      onClick={onOpenMarket}
      disabled={!available}
    >
      <span className={styles.wildMarketIcon} aria-hidden>
        <svg
          viewBox="0 0 24 24"
          width="22"
          height="22"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="9" cy="20" r="1.4" />
          <circle cx="17" cy="20" r="1.4" />
          <path d="M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h7.6a2 2 0 0 0 2-1.5L21 8H6" />
        </svg>
      </span>

      <span className={styles.wildProgressTrack} aria-hidden>
        <span
          className={styles.wildProgressFill}
          style={{ height: `${pct.toFixed(2)}%` }}
        >
          <span className={styles.wildBubble1} />
          <span className={styles.wildBubble2} />
          <span className={styles.wildBubble3} />
          <span className={styles.wildBubble4} />
        </span>
      </span>
    </button>
  );
};

export default WildProgressBar;
